import { useEffect, useRef, useState } from 'react';
import { addLook, stickIntent } from '../../game/input.js';

export function useTouchMode() {
  const [touch, setTouch] = useState(
    () => globalThis.matchMedia?.('(pointer: coarse)').matches ?? false,
  );
  useEffect(() => {
    const detect = (event) => {
      if (event.pointerType === 'touch') setTouch(true);
    };
    const media = window.matchMedia('(pointer: coarse)');
    const change = () => {
      if (media.matches) setTouch(true);
    };
    window.addEventListener('pointerdown', detect, { passive: true });
    media.addEventListener('change', change);
    return () => {
      window.removeEventListener('pointerdown', detect);
      media.removeEventListener('change', change);
    };
  }, []);
  return touch;
}

export function TouchControls({ input, gauntlet }) {
  const stick = useRef(null);
  const look = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const [sprinting, setSprinting] = useState(false);
  const release = (event) => {
    if (stick.current?.id !== event.pointerId) return;
    stick.current = null;
    input.stick = { x: 0, y: 0 };
    setKnob({ x: 0, y: 0 });
  };
  return (
    <div className="touch-controls">
      <div
        className="touch-look"
        aria-label="Drag to look"
        onPointerDown={(event) => {
          if (look.current) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          look.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
        }}
        onPointerMove={(event) => {
          if (look.current?.id !== event.pointerId) return;
          addLook(input, event.clientX - look.current.x, event.clientY - look.current.y);
          look.current.x = event.clientX;
          look.current.y = event.clientY;
        }}
        onPointerUp={(event) => {
          if (look.current?.id === event.pointerId) look.current = null;
        }}
        onPointerCancel={() => {
          look.current = null;
        }}
        onLostPointerCapture={() => {
          look.current = null;
        }}
      />
      <div
        className="move-stick"
        role="group"
        aria-label="Movement stick"
        onPointerDown={(event) => {
          if (stick.current) return;
          const rect = event.currentTarget.getBoundingClientRect();
          stick.current = {
            id: event.pointerId,
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
          input.stick = stickIntent(
            event.clientX - stick.current.x,
            event.clientY - stick.current.y,
          );
        }}
        onPointerMove={(event) => {
          if (stick.current?.id !== event.pointerId) return;
          const x = event.clientX - stick.current.x,
            y = event.clientY - stick.current.y;
          input.stick = stickIntent(x, y);
          const scale = Math.min(1, 46 / (Math.hypot(x, y) || 1));
          setKnob({ x: x * scale, y: y * scale });
        }}
        onPointerUp={release}
        onPointerCancel={release}
        onLostPointerCapture={release}
      >
        <span style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }} />
        <small>Move</small>
      </div>
      <div className="touch-actions">
        <button
          aria-pressed={sprinting}
          onClick={() => {
            input.sprint = !input.sprint;
            setSprinting(input.sprint);
          }}
        >
          Sprint
        </button>
        <button onClick={() => input.actions.add('jump')}>Jump</button>
        <button className="interact-button" onClick={() => input.actions.add('interact')}>
          Interact
        </button>
        {gauntlet && (
          <>
            <button onClick={() => input.actions.add('freeze')}>Freeze</button>
            <button onClick={() => input.actions.add('rewind')}>Rewind</button>
          </>
        )}
      </div>
    </div>
  );
}
